/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Persisted identifiers for page-reload queue recovery, isolated in a leaf
 * module with no queue / service dependencies. The explicit "stop playback"
 * action clears the snapshot through here so that removal is synchronous and
 * independent of the lazily-loaded restore module — a Stop before that module
 * resolves would otherwise leave the snapshot behind and restore media on the
 * next startup.
 */

import { getPlaybackEnv } from '../env/playbackEnvRef'
import { PLAYBACK_STORAGE_KEYS } from '../env/playbackStorageKeys'

const STORAGE_KEY = PLAYBACK_STORAGE_KEYS.queueRestore

/** The minimal identifiers needed to re-pull and re-anchor a queue on reload. */
export interface QueueRestoreSnapshot {
    readonly queueId: string
    readonly entityReferenceId: string
    readonly positionSec: number
}

// Synchronous on purpose: an async persistent item would race boot restore and tab teardown.
export function writeQueueRestoreSnapshot(snapshot: QueueRestoreSnapshot): void {
    try {
        getPlaybackEnv().store.setItem(STORAGE_KEY, JSON.stringify(snapshot))
    } catch {
        /* private-mode / quota — restore is best-effort. */
    }
}

export function readQueueRestoreSnapshot(): QueueRestoreSnapshot | null {
    try {
        const raw = getPlaybackEnv().store.getItem(STORAGE_KEY)
        if (!raw) return null
        const parsed: unknown = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return null
        const { queueId, entityReferenceId, positionSec } = parsed as Record<string, unknown>
        if (typeof queueId !== 'string' || typeof entityReferenceId !== 'string') return null
        return {
            queueId,
            entityReferenceId,
            positionSec: typeof positionSec === 'number' && positionSec > 0 ? positionSec : 0,
        }
    } catch {
        return null
    }
}

export function clearQueueRestoreSnapshot(): void {
    try {
        getPlaybackEnv().store.removeItem(STORAGE_KEY)
    } catch {
        /* private-mode — nothing to clear. */
    }
}
